import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AnimatePresence,
  MotionValue,
  motion,
  useMotionValue,
  useReducedMotion,
  useTransform,
} from "framer-motion";
import { FolderOpen, BadgeDollarSign, Mail, MonitorPlay, Play, ArrowRight } from "lucide-react";
import api from "@/api";
import { Creator, HomeContent } from "@/types";
import { OSWindow } from "@/components/os/Window";
import { Taskbar } from "@/components/os/Taskbar";
import { DesktopIcon } from "@/components/os/DesktopIcon";
import { Marquee } from "@/components/os/Marquee";
import { PixelHeart, PixelStar, PixelBolt } from "@/components/os/Stickers";
import { useIsMobile } from "@/hooks/useIsMobile";

const CAMCORDER =
  "https://static.prod-images.emergentagent.com/jobs/351c4e11-bf4e-4a4c-920b-34c1da0a7c7c/images/83a65c476aa454230c2141e1156d455d02d93650eb0eed2317fe3f7e6ade7ad9.jpeg";
const CASSETTE = "/assets/cassette.png";

const DEFAULT_CONTENT: HomeContent = {
  tagline:
    "Your one stop editing shop - long-form, short-form, thumbnails & uploads, all handled by us for you. Film it, send it, and we'll handle the rest!",
  hero_lines: ["YOUR CHANNEL'S", "NEW FAVORITE", "CO-PRODUCER."],
  marquee_items: [
    "RETENTION EDITING", "SOUND DESIGN", "SCROLL-STOPPING HOOKS", "CLICK-WORTHY THUMBNAILS",
    "72H TURNAROUND", "COLOR GRADE", "MOTION GFX", "ZERO FLUFF",
  ],
  status_text: "NOW BOOKING",
  status_published: true,
  rates_note: "",
  inquiry_intro: "",
};

type WinKey = "welcome" | "featured";

const RevealLines = ({ lines }: { lines: string[] }) => {
  const reduced = useReducedMotion();
  const isMobile = useIsMobile();
  const shown = isMobile ? lines.flatMap((l) => (l.length > 13 ? l.split(" ") : [l])) : lines;
  return (
    <h1
      data-testid="hero-headline"
      className="font-pixel text-[17px] leading-[1.7] sm:text-3xl sm:leading-[1.7] lg:text-4xl lg:leading-[1.65]"
    >
      {shown.map((l, i) => (
        <span key={i} className="block overflow-hidden pb-1">
          <motion.span
            className="block"
            initial={reduced ? false : { y: "115%" }}
            animate={{ y: 0 }}
            transition={{ delay: 0.25 + i * 0.16, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            {l}
          </motion.span>
        </span>
      ))}
    </h1>
  );
};

const Parallax = ({
  mx, my, depth, className, children,
}: { mx: MotionValue<number>; my: MotionValue<number>; depth: number; className?: string; children: React.ReactNode }) => {
  const x = useTransform(mx, (v) => v * depth);
  const y = useTransform(my, (v) => v * depth);
  return (
    <motion.div style={{ x, y }} className={className} aria-hidden>
      {children}
    </motion.div>
  );
};

export default function HomePage() {
  const nav = useNavigate();
  const deskRef = useRef<HTMLDivElement>(null);
  const topZ = useRef(2);
  const reduced = useReducedMotion();
  const isMobile = useIsMobile();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);

  const [content, setContent] = useState<HomeContent>(DEFAULT_CONTENT);
  const [featured, setFeatured] = useState<Creator | null>(null);
  const [wins, setWins] = useState<Record<WinKey, { open: boolean; min: boolean; z: number }>>({
    welcome: { open: true, min: false, z: 2 },
    featured: { open: true, min: false, z: 1 },
  });

  useEffect(() => {
    api.get("/content/home").then((r) => setContent({ ...DEFAULT_CONTENT, ...r.data })).catch(() => {});
    api.get("/creators").then((r) => {
      const list: Creator[] = r.data;
      setFeatured(list.find((c) => c.featured) || list[0] || null);
    }).catch(() => {});
  }, []);

  const focusW = (k: WinKey) => {
    topZ.current += 1;
    const z = topZ.current;
    setWins((p) => ({ ...p, [k]: { ...p[k], z } }));
  };
  const closeW = (k: WinKey) => setWins((p) => ({ ...p, [k]: { ...p[k], open: false } }));
  const minW = (k: WinKey) => setWins((p) => ({ ...p, [k]: { ...p[k], min: true } }));
  const restoreW = (k: WinKey) => {
    topZ.current += 1;
    const z = topZ.current;
    setWins((p) => ({ ...p, [k]: { open: true, min: false, z } }));
  };

  const onMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (reduced) return;
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 22);
    my.set(((e.clientY - r.top) / r.height - 0.5) * 22);
  };

  const taskbarWins = (Object.keys(wins) as WinKey[])
    .filter((k) => wins[k].open || wins[k].min)
    .filter((k) => k === "welcome" || (!isMobile && !!featured))
    .map((k) => ({
      id: k,
      title: k === "welcome" ? "late_nights.exe" : "now_featuring.mov",
      active: wins[k].open && !wins[k].min,
      onClick: () => (wins[k].min || !wins[k].open ? restoreW(k) : focusW(k)),
    }));

  return (
    <div ref={deskRef} onPointerMove={onMove} className="desktop-bg relative min-h-screen overflow-x-hidden md:overflow-hidden">
      <Parallax mx={mx} my={my} depth={1} className="pointer-events-none absolute right-[10%] top-10 hidden md:block">
        <PixelHeart px={6} />
      </Parallax>
      <Parallax mx={mx} my={my} depth={-1.4} className="pointer-events-none absolute left-[38%] top-[8%] hidden md:block">
        <PixelStar px={5} />
      </Parallax>
      <Parallax mx={mx} my={my} depth={0.7} className="pointer-events-none absolute bottom-40 left-[45%] hidden md:block">
        <PixelBolt px={5} className="opacity-60" />
      </Parallax>
      <Parallax mx={mx} my={my} depth={-0.8} className="pointer-events-none absolute bottom-48 right-[24%] hidden md:block">
        <PixelHeart px={4} color="#7C5CBF" className="opacity-70" />
      </Parallax>

      <div className="absolute left-2.5 top-4 z-10 flex flex-col gap-4 md:left-5 md:top-6 md:gap-6">
        <DesktopIcon testid="desktop-icon-welcome" label="welcome.exe" icon={<MonitorPlay size={22} />} onOpen={() => restoreW("welcome")} />
        <DesktopIcon testid="desktop-icon-portfolio" label="portfolio" icon={<FolderOpen size={22} />} onOpen={() => nav("/portfolio")} />
        <DesktopIcon testid="desktop-icon-rates" label="rate_card" icon={<BadgeDollarSign size={22} />} onOpen={() => nav("/rates")} />
        <DesktopIcon testid="desktop-icon-inquire" label="say hello" icon={<Mail size={22} />} onOpen={() => nav("/inquire")} />
      </div>

      <div className="flex flex-col items-stretch gap-6 pb-48 pl-[88px] pr-3 pt-4 md:block md:p-0">
        <AnimatePresence>
          {wins.welcome.open && !wins.welcome.min && (
            <OSWindow
              id="welcome"
              title="late_nights.exe — Welcome"
              tint="lavender"
              z={wins.welcome.z}
              constraintsRef={deskRef}
              onClose={() => closeW("welcome")}
              onMinimize={() => minW("welcome")}
              onFocus={() => focusW("welcome")}
              className="w-full md:absolute md:left-[8%] md:top-14 md:w-[620px] lg:left-[12%]"
            >
              <div className="relative p-5 sm:p-8">
                <img
                  src={CASSETTE}
                  alt=""
                  className="absolute top-2 right-3 hidden w-12 rotate-[20deg] sm:right-4 sm:block sm:w-16"
                  style={{ filter: "drop-shadow(3px 3px 0 rgba(23,17,15,0.85))" }}
                />
                <p className="whitespace-nowrap font-spacemono text-[11px] text-ink/60">
                  C:\late_nights&gt; run welcome.exe <span className="animate-blink">▌</span>
                </p>
                <div className="mt-4 sm:mt-5">
                  <RevealLines lines={content.hero_lines} />
                </div>
                <p data-testid="hero-tagline" className="mt-4 max-w-md text-sm font-medium leading-relaxed text-ink/80 sm:mt-5 sm:text-base">
                  {content.tagline}
                </p>
                {content.status_published && (
                  <div data-testid="status-tag" className="mx-auto mt-7 flex w-fit rotate-[-2deg] items-center gap-2 border-2 border-ink bg-butter px-3 py-1.5 shadow-hard-sm sm:mx-0 sm:mt-6">
                    <span className="status-dot h-2 w-2 bg-ink" aria-hidden />
                    <span className="font-spacemono text-[11px] font-bold tracking-widest">STATUS: {content.status_text}</span>
                  </div>
                )}
                <div className="mt-8 flex flex-wrap justify-center gap-3 sm:mt-7 sm:justify-start">
                  <button data-testid="cta-portfolio" onClick={() => nav("/portfolio")} className="btn-chunky bg-mint px-4 py-2.5 text-xs">
                    SEE THE WORK
                  </button>
                  <button data-testid="cta-rates" onClick={() => nav("/rates")} className="btn-chunky bg-white px-4 py-2.5 text-xs">
                    RATE CARD
                  </button>
                  <button data-testid="cta-inquire" onClick={() => nav("/inquire")} className="btn-chunky bg-lnpink px-4 py-2.5 text-xs">
                    START A PROJECT <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            </OSWindow>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {!isMobile && featured && wins.featured.open && !wins.featured.min && (
            <OSWindow
              id="featured"
              title="now_featuring.mov"
              tint="mint"
              z={wins.featured.z}
              constraintsRef={deskRef}
              onClose={() => closeW("featured")}
              onMinimize={() => minW("featured")}
              onFocus={() => focusW("featured")}
              className="w-full md:absolute md:right-[7%] md:top-28 md:w-[360px]"
            >
              <div className="p-4 sm:p-5">
                <button
                  data-testid="featured-play"
                  onClick={() => nav(`/portfolio/${featured.slug}`)}
                  className="group relative block w-full overflow-hidden border-[3px] border-ink shadow-hard-sm"
                >
                  <img src={CAMCORDER} alt={`${featured.name} — featured edit`} className="h-40 w-full object-cover" />
                  <span className="scanlines absolute inset-0" aria-hidden />
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full border-[3px] border-ink bg-white transition-transform duration-150 group-hover:scale-110">
                      <Play size={18} className="ml-0.5 fill-ink text-ink" />
                    </span>
                  </span>
                </button>
                <p data-testid="featured-name" className="mt-4 font-pixel text-[11px] leading-relaxed">{featured.name.toUpperCase()}</p>
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  <span className="border-2 border-ink bg-white px-1.5 py-0.5 font-spacemono text-[10px] font-bold">{featured.niche}</span>
                  <span className="border-2 border-ink bg-butter px-1.5 py-0.5 font-spacemono text-[10px] font-bold">{featured.metric}</span>
                </div>
                <button
                  data-testid="featured-showreel"
                  onClick={() => nav(`/portfolio/${featured.slug}`)}
                  className="btn-chunky mt-4 w-full bg-white px-3 py-2 text-[11px]"
                >
                  LOAD SHOWREEL <ArrowRight size={13} />
                </button>
              </div>
            </OSWindow>
          )}
        </AnimatePresence>
      </div>

      <div className="fixed inset-x-0 bottom-12 z-40">
        <Marquee items={content.marquee_items} />
      </div>

      <Taskbar windows={taskbarWins} />
    </div>
  );
}
